import React from 'react';
import { inject, observer } from 'mobx-react';
import type { AppStore } from '@stores/app/store';
import type { SiteAction } from '@stores/site/action';
import type { UserAction } from '@stores/user/action';
import type { PhotographyAction } from '@stores/photography/action';
import { BasePage } from '@components/Base/BasePage';
import styles from './index.module.scss';
import type { BasePageProps, BasePageState } from '../../../../types/base';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { BASE_IMG_URL, commonImage, photographyVideoImage } from '@images/imageUrls';
import PhotographyVideoPlayer from '@components/photographyVideoPlayer';
import { isVideoWork, getPhotographyVideoUrl, getVideoAiText, canDeletePhotographyWork } from '@utils/photographyVideo';
import Toast from '@components/Toast';
import WaterfallFlow from '@components/waterfallFlow';
import CommonPopupNew from '@components/commonPopupNew';
import classNames from 'classnames';
import SharePosterPopup from '../../../photography/layout/h5/components/SharePosterPopup';
import Avatar from '@components/avatar';

interface RouteParams {
    code?: string;
}

interface PhotographySearchH5Props extends BasePageProps, RouteComponentProps<RouteParams> {
    app?: AppStore;
    site?: SiteAction;
    user?: UserAction;
    photography?: PhotographyAction;
}

interface PhotographySearchH5State extends BasePageState {
    downloadAppStatus: boolean;
    headerShowStatus: boolean;
    photographyDetail: any;
    keyword: string;
    searchKeyword: string;
    hasSearched: boolean;
    photoList: any[];
    currentPage: number;
    hasMore: boolean;
    resultCount: number;
    tipPopupStatus: boolean;
    sharePosterVisible: boolean;
    shareWorkItem: any;
    currentVoteItem: any;
    currentOpenAppType: number;
    currentImgDetail: any;
    currentImgDetailShowStatus: boolean;
    manageWorkPopupStatus: boolean;
    manageCurrentWorkData: any;
    deleteWorkPopupStatus: boolean;
    closeDetailTrigger: number;
    deletingWork: boolean;
}

@inject('app', 'site', 'user', 'photography')
@observer
class PhotographySearchH5Page extends BasePage<PhotographySearchH5Props, PhotographySearchH5State> {
    photographyCode: any = '';
    private detailRequestId = 0;
    private videoProgressRef = React.createRef<HTMLDivElement>();
    private votingWorkId: any = null;
    private deletingWorkId: any = null;

    state: PhotographySearchH5State = {
        loading: false,
        error: null,
        pageLoaded: false,
        downloadAppStatus: false,
        headerShowStatus: false,
        photographyDetail: {},
        keyword: '',
        searchKeyword: '',
        hasSearched: false,
        photoList: [],
        currentPage: 0,
        hasMore: false,
        resultCount: 0,
        tipPopupStatus: false,
        sharePosterVisible: false,
        shareWorkItem: {},
        currentVoteItem: {},
        currentOpenAppType: 2,
        currentImgDetail: {},
        currentImgDetailShowStatus: false,
        manageWorkPopupStatus: false,
        manageCurrentWorkData: {},
        deleteWorkPopupStatus: false,
        closeDetailTrigger: 0,
        deletingWork: false,
    };

    componentDidMount() {
        const { site, match, location } = this.props;
        const searchParams = new URLSearchParams(location.search);
        this.photographyCode = match.params.code || searchParams.get('code') || 0;
        this.getPhotoDetailData();
        if (site) {
            this.setState({
                downloadAppStatus: site.getAppDownloadShowStatus(),
                headerShowStatus: site.getShowHeader(),
            }, () => {
                site.setShowHeader(false);
                site.setAppDownloadShowStatus(false);
            });
        }
        super.componentDidMount();
    }

    componentWillUnmount() {
        this.detailRequestId++;
        const { site } = this.props;
        const { downloadAppStatus, headerShowStatus } = this.state;
        if (site) {
            site.setShowHeader(headerShowStatus);
            site.setAppDownloadShowStatus(downloadAppStatus);
        }
    }

    getPhotoDetailData = () => {
        const { photography } = this.props;
        if (!photography || !this.photographyCode) return;
        photography.getPhotographyContestsDetail(this.photographyCode).then((res: any) => {
            if (res.code == 0) {
                this.setState({ photographyDetail: res.data || {} });
            }
        });
    };

    getPhotographyWorksDetailData = (workId: any) => {
        const requestId = ++this.detailRequestId;
        const { photography } = this.props;
        if (!photography) return;
        photography.getPhotographyWorksDetail(workId).then((res: any) => {
            if (requestId !== this.detailRequestId) return;
            if (res.code == 0) {
                this.setState({
                    currentImgDetail: res.data || {},
                    currentImgDetailShowStatus: true,
                });
                return;
            }
            // Toast.info('服务器错误，请稍后重试');
        });
    };

    getSearchWorksList = (isLoadMore = false) => {
        const { photography } = this.props;
        const { loading, currentPage, photoList, searchKeyword, photographyDetail } = this.state;
        if (!photography || loading || !searchKeyword || !this.photographyCode) return;

        const page = isLoadMore ? currentPage + 1 : 1;
        const params = {
            sort: 'id',
            'page[number]': page,
            'page[size]': 20,
            'filter[name]': searchKeyword,
        };

        this.setState({ loading: true });
        photography.getPhotographyContestsWorks(photographyDetail?.id, params).then((res: any) => {
            if (res.code == 0) {
                const newData = res.data?.data || [];
                const pageInfo = res.data?.page || {};
                const nextList = isLoadMore ? [...photoList, ...newData] : newData;
                const resultCount = Number(pageInfo.totalCount ?? pageInfo.count ?? pageInfo.total ?? nextList.length) || 0;
                const hasMore = typeof pageInfo.hasMore === 'boolean' ? pageInfo.hasMore : newData.length >= 20;
                this.setState({
                    photoList: nextList,
                    currentPage: page,
                    hasMore,
                    resultCount: resultCount || nextList.length,
                    hasSearched: true,
                });
                return;
            }
            Toast.info(res?.msg || res?.message || '搜索失败');
        }).finally(() => {
            this.setState({ loading: false });
        });
    };

    handleSearch = () => {
        const keyword = this.state.keyword.trim();
        if (!keyword || this.state.loading) return;
        if (!this.state.photographyDetail?.id) {
            Toast.info('缺少活动CODE，无法搜索');
            return;
        }
        this.setState({
            searchKeyword: keyword,
            hasSearched: true,
            photoList: [],
            currentPage: 0,
            hasMore: false,
            resultCount: 0,
        }, () => {
            this.getSearchWorksList();
        });
    };

    handleLoadMore = () => {
        const { loading, hasMore, searchKeyword } = this.state;
        if (loading || !hasMore || !searchKeyword) return;
        this.getSearchWorksList(true);
    };

    handleKeywordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const keyword = e.target.value;
        if (!keyword.trim()) {
            this.setState({
                keyword,
                searchKeyword: '',
                hasSearched: false,
                photoList: [],
                currentPage: 0,
                hasMore: false,
                resultCount: 0,
            });
            return;
        }
        this.setState({ keyword });
    };

    handleKeywordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            this.handleSearch();
        }
    };

    clearKeyword = () => {
        this.setState({
            keyword: '',
            searchKeyword: '',
            hasSearched: false,
            photoList: [],
            currentPage: 0,
            hasMore: false,
            resultCount: 0,
        });
    };

    onHidePopup = () => {
        this.setState({
            tipPopupStatus: false,
            manageWorkPopupStatus: false,
            deleteWorkPopupStatus: false,
        });
    };

    handleOpenAppConfirm = () => {
        const { currentOpenAppType, currentVoteItem } = this.state;
        this.setState({ tipPopupStatus: false });
        if (currentOpenAppType == 2 && currentVoteItem?.id) {
            const deepLink = `vivoclub://com.vivo.club/deep?page=photography_compositon_detail&id=${currentVoteItem.id}`;
            window.location.href = deepLink;
            // 3秒后如果应用没有打开，跳转到下载页
            setTimeout(() => {
                this.props.history.push(`/downloadAppEnter?deepLink=${encodeURIComponent(deepLink)}`);
                // window.open(`https://m.malink.cn/s/ABbE73`)
            }, 3000);
        }
    };

    voteClick = (item: any) => {
        if (Number(this.state.photographyDetail.status) === 2) {
            Toast.info('活动已结束');
            return;
        }
        if (item?.isVoted) {
            Toast.info('每天只能为同一个作品投一次');
            return;
        }
        if (isVideoWork(item)) {
            this.voteVideoWork(item);
            return;
        }
        this.setState({
            tipPopupStatus: true,
            currentVoteItem: item,
            currentOpenAppType: 2,
        });
    };

    voteVideoWork = async (item: any) => {
        const { photography, user } = this.props;
        if (!user?.userInfo?.code) {
            if (user && !user.checkLogin()) return;
            Toast.info('请先登录!');
            return;
        }
        if (!photography || !item?.id || this.votingWorkId) return;
        this.votingWorkId = item.id;
        try {
            const res = await photography.submitPhotographyWorksVoteData(item.id);
            if (res.code != 0) {
                Toast.info(res.msg || res.message || '投票失败，请稍后重试');
                return;
            }
            const updateWork = work => String(work?.id) === String(item.id)
                ? { ...work, isVoted: true, voteCount: Number(work.voteCount || 0) + 1 } : work;
            this.setState(state => ({ photoList: state.photoList.map(updateWork), currentImgDetail: updateWork(state.currentImgDetail) }));
            Toast.info('投票成功，感谢你的宝贵一票');
        } catch {
            Toast.info('投票失败，请稍后重试');
        } finally {
            this.votingWorkId = null;
        }
    };

    getShareUrl = (item: any) => {
        const workId = item?.id ? `?workId=${item.id}` : '';
        return `${window.location.origin}/photography/${this.photographyCode}${workId}`;
    };

    getTopVoteCount = () => {
        const { photoList } = this.state;
        if (!Array.isArray(photoList) || photoList.length === 0) {
            return 0;
        }
        return photoList.reduce((max, current) => Math.max(max, Number(current?.voteCount || 0)), 0);
    };

    closeSharePoster = () => {
        this.setState({
            sharePosterVisible: false,
            shareWorkItem: {},
        });
    };

    canvassForVotesClick = (item: any) => {
        if (Number(this.state.photographyDetail.status) === 2) {
            Toast.info('活动已结束');
            return;
        }
        if (isVideoWork(item) && Number(item.status) !== 1) return;
        this.setState({
            sharePosterVisible: true,
            shareWorkItem: item || {},
        });
    };

    currentItemClick = (item: any) => {
        if (!item?.id) return;
        this.getPhotographyWorksDetailData(item.id);
    };

    manageCurrentWork = (data: any) => {
        if (!canDeletePhotographyWork(data, this.props.user?.userInfo, this.state.photographyDetail.status)) return;
        this.setState({
            manageCurrentWorkData: data,
            manageWorkPopupStatus: true,
        });
    };

    deleteWorkOpenPopup = () => {
        this.setState({
            deleteWorkPopupStatus: true,
            manageWorkPopupStatus: false,
        });
    };

    closeCurrentImgDetail = () => {
        this.detailRequestId++;
        this.setState({ currentImgDetail: {}, currentImgDetailShowStatus: false, closeDetailTrigger: Date.now() });
    };

    deleteWorkConfirm = async () => {
        const { manageCurrentWorkData, photographyDetail } = this.state;
        const { photography } = this.props;
        if (!photography || !manageCurrentWorkData?.id || this.deletingWorkId || !canDeletePhotographyWork(manageCurrentWorkData, this.props.user?.userInfo, photographyDetail.status)) return;
        this.deletingWorkId = manageCurrentWorkData.id;
        this.setState({ deletingWork: true });
        try {
            const res = await photography.deletePhotographyWorkData(manageCurrentWorkData.id);
            if (res.code == 0) {
                this.setState(state => ({
                    manageWorkPopupStatus: false,
                    deleteWorkPopupStatus: false,
                    photoList: state.photoList.filter(item => String(item.id) !== String(manageCurrentWorkData.id)),
                    resultCount: Math.max(0, state.resultCount - 1),
                }));
                this.closeCurrentImgDetail();
                Toast.info('作品删除成功');
            } else {
                Toast.info(res.msg || res.message || '删除失败，请稍后重试');
            }
        } catch {
            Toast.info('删除失败，请稍后重试');
        } finally {
            this.deletingWorkId = null;
            this.setState({ deletingWork: false });
        }
    };

    render() {
        const {
            keyword,
            searchKeyword,
            hasSearched,
            photoList,
            loading,
            hasMore,
            resultCount,
            tipPopupStatus,
            photographyDetail,
            sharePosterVisible,
            shareWorkItem,
            currentVoteItem,
            currentImgDetail,
            currentImgDetailShowStatus,
            manageWorkPopupStatus,
            deleteWorkPopupStatus,
            closeDetailTrigger,
        } = this.state;
        const canSearch = !!keyword.trim();
        const showEmptyResults = hasSearched && !loading && photoList.length === 0;
        const isCurrentVideo = isVideoWork(currentImgDetail);
        const isVideoManagement = isVideoWork(this.state.manageCurrentWorkData);

        return (
            <div className={styles.wrapper}>
                <div className={styles.header}>
                    <button type="button" className={styles.backBtn} onClick={() => window.history.back()} aria-label="返回">
                        <img src={commonImage.back} alt="" />
                    </button>
                    <div className={styles.searchBar}>
                        <div className={styles.searchInputWrap}>
                            <img src={`${BASE_IMG_URL}/common/search.png`} alt="搜索" className={styles.searchIcon} />
                            <input
                                value={keyword}
                                onChange={this.handleKeywordChange}
                                onKeyDown={this.handleKeywordKeyDown}
                                placeholder="搜索作品名称或作品ID"
                                className={styles.searchInput}
                            />
                            {!!keyword && <button type="button" className={styles.clearBtn} onClick={this.clearKeyword} aria-label="清空"><img src={`${BASE_IMG_URL}/common/close.png`} alt="" /></button>}
                        </div>
                        <button
                            type="button"
                            className={styles.searchBtn}
                            onClick={this.handleSearch}
                            disabled={!canSearch}
                        >
                            搜索
                        </button>
                    </div>
                </div>

                {hasSearched && (
                    <>
                        {/* <div className={styles.resultCount}>搜索到作品({resultCount})</div> */}
                        <div className={classNames(styles.resultList, showEmptyResults && styles.emptyResultList)}>
                            {showEmptyResults ? <div className={styles.emptyResults}>
                                <img src={`${BASE_IMG_URL}/common/null4.png`} alt="" />
                                <div className={styles.emptyText}>未搜索到相关作品</div>
                            </div> : <WaterfallFlow
                                data={photoList}
                                loading={loading}
                                hasMore={hasMore}
                                onLoadMore={this.handleLoadMore}
                                searchKeyword={searchKeyword}
                                voteClick={this.voteClick}
                                canvassForVotesClick={this.canvassForVotesClick}
                                photographyDetail={photographyDetail}
                                currentItemClick={this.currentItemClick}
                                manageCurrentWork={this.manageCurrentWork}
                                closeDetailTrigger={closeDetailTrigger}
                                emptyText="未搜索到相关作品"
                                emptyIcon={`${BASE_IMG_URL}/common/null4.png`}
                            />}
                        </div>
                    </>
                )}

                <CommonPopupNew
                    visible={manageWorkPopupStatus}
                    title="更多操作"
                    confirmText="删除作品"
                    popupContentClassName={isVideoManagement ? classNames(styles.videoActionPopup, styles.videoMorePopup) : undefined}
                    onCancelChange={this.onHidePopup}
                    onConfirmButtonChange={this.deleteWorkOpenPopup}
                />

                <CommonPopupNew
                    visible={deleteWorkPopupStatus}
                    title={isVideoManagement ? '确认删除此作品？' : '删除作品'}
                    popupContentClassName={isVideoManagement ? styles.videoActionPopup : undefined}
                    confirmText={this.state.deletingWork ? '删除中...' : '确定'}
                    confirmDisabled={this.state.deletingWork}
                    onCancelChange={this.onHidePopup}
                    onConfirmButtonChange={this.deleteWorkConfirm}
                >
                    {!isVideoManagement && <div className={styles.popupDeleteTip}>确定要删除此作品吗？</div>}
                </CommonPopupNew>

                {currentImgDetailShowStatus && <div className={classNames(styles.waterfallCardDetail, isCurrentVideo && styles.videoDetail, isCurrentVideo && getVideoAiText(currentImgDetail.aigcStatus) && styles.videoHasAi)}>
                    <div className={styles.detailTiTleBox}>
                        <img
                            src={isCurrentVideo ? photographyVideoImage.back : `${BASE_IMG_URL}/photography/back.png`}
                            alt="关闭作品详情"
                            className={styles.detailTiTleBack}
                            onClick={this.closeCurrentImgDetail}
                        />
                        <div className={styles.detailTiTle}>{isCurrentVideo ? `《${String(currentImgDetail.name || '').replace(/^《|》$/g, '')}》` : currentImgDetail.name}</div>
                        {canDeletePhotographyWork(currentImgDetail, this.props.user?.userInfo, photographyDetail.status) ? (
                            <img
                                src={isCurrentVideo ? photographyVideoImage.more : `${BASE_IMG_URL}/photography/icon8.png`}
                                alt="更多操作"
                                className={styles.detailTiTleBack}
                                onClick={() => this.manageCurrentWork(currentImgDetail)}
                            />
                        ) : isCurrentVideo && <span className={styles.headerSpace} />}
                    </div>
                    <div className={styles.detailImgBox}>
                        {isCurrentVideo ? <PhotographyVideoPlayer key={String(currentImgDetail.id)} src={getPhotographyVideoUrl(currentImgDetail)}
                            poster={currentImgDetail.image} title={currentImgDetail.name}
                            width={currentImgDetail.video?.width || currentImgDetail.width} height={currentImgDetail.video?.height || currentImgDetail.height}
                            progressHost={this.videoProgressRef}
                                paused={manageWorkPopupStatus || deleteWorkPopupStatus || sharePosterVisible || tipPopupStatus}
                        /> : <img src={currentImgDetail.image} alt="" className={styles.detailImg} />}
                        {!isCurrentVideo && currentImgDetail.aigcStatus == 1 && <div className={styles.aiTip}>图片属于AI生成</div>}
                        {!isCurrentVideo && currentImgDetail.aigcStatus == 2 && <div className={styles.aiTip}>作者声明：图片由AI生成</div>}
                        {!isCurrentVideo && currentImgDetail.aigcStatus == 3 && <div className={styles.aiTip}>图片疑似使用AI生成技术，请谨慎甄别</div>}
                    </div>
                    <div className={styles.detailInfoBox}>
                        <div className={styles.userInfo}>
                            {currentImgDetail.user?.avatar && (
                                <Avatar
                                    avatarUrl={currentImgDetail.user?.avatar}
                                    userCode={currentImgDetail.user?.code}
                                    size={60}
                                    titleMark={currentImgDetail.user?.titleMark}
                                        level={currentImgDetail.user?.level}
                                    officialSize={24}
                                />
                            )}
                            <div className={styles.userNameBox}>
                                <span className={styles.nickname}>{currentImgDetail.user?.nickname}</span>
                                <span className={styles.titleName}>{currentImgDetail.user?.titleName}</span>
                            </div>
                        </div>
                        <div className={styles.numBox}>
                            {Number(currentImgDetail.ranking) > 0 && (!isCurrentVideo || Number(currentImgDetail.status) === 1) && <div className={styles.waterfallRanking}>第{currentImgDetail.ranking > 100 ? '100+' : currentImgDetail.ranking}名</div>}
                            <div className={styles.waterfallNumber}>ID:{currentImgDetail.number}</div>
                            {(!isCurrentVideo || Number(currentImgDetail.status) === 1) && <div className={styles.waterfallVoteCount}>{currentImgDetail.voteCount || 0}票</div>}
                        </div>
                    </div>
                    {isCurrentVideo && getVideoAiText(currentImgDetail.aigcStatus) && <div className={styles.videoAi}>
                        <img src={photographyVideoImage.ai} alt="" />{getVideoAiText(currentImgDetail.aigcStatus)}
                    </div>}
                    {isCurrentVideo && <div ref={this.videoProgressRef} className={styles.videoProgress} />}
                    {(this.props.user?.userInfo?.code != currentImgDetail.user?.code) && <div className={styles.detailBtnBox}>
                        <div className={classNames(styles.canvass, photographyDetail.status == 2 ? styles.canvassGray : '')} onClick={() => this.canvassForVotesClick(currentImgDetail)}>{isCurrentVideo ? '为作者拉票' : '为TA拉票'}</div>
                        <div className={classNames(styles.openAppVote, (photographyDetail.status == 2 || (isCurrentVideo && currentImgDetail.isVoted)) ? styles.openAppVoteGray : '')} onClick={() => this.voteClick(currentImgDetail)}>{isCurrentVideo ? currentImgDetail.isVoted ? '今日已投' : '投票' : '打开App端投票'}</div>
                    </div>}
                    {this.props.user?.userInfo?.code == currentImgDetail.user?.code && <>
                        {currentImgDetail.status == 1 && <div className={classNames(styles.mycanvass, photographyDetail.status == 2 ? styles.mycanvassGray : '')} onClick={() => this.canvassForVotesClick(currentImgDetail)}>我要拉票</div>}
                        {currentImgDetail.status == 0 && <div className={classNames(styles.waterfallStatus)}>
                            <img
                                src={isCurrentVideo ? photographyVideoImage.pending : `${BASE_IMG_URL}/photography/icon6.png`}
                                alt="审核中"
                                className={styles.icon}
                            />审核中
                        </div>}
                        {currentImgDetail.status == 2 && <div className={classNames(styles.waterfallStatus, styles.waterfallStatus2)}>
                            <img
                                src={isCurrentVideo ? photographyVideoImage.warning : `${BASE_IMG_URL}/photography/icon7.png`}
                                alt=""
                                className={styles.icon}
                            />
                            审核未通过
                        </div>}
                        {currentImgDetail.status == 3 && <div className={classNames(styles.waterfallStatus, styles.waterfallStatus2)}>
                            <img
                                src={isCurrentVideo ? photographyVideoImage.warning : `${BASE_IMG_URL}/photography/icon8.png`}
                                alt=""
                                className={styles.icon}
                            />已下架
                        </div>}
                    </>}
                </div>}

                <CommonPopupNew
                    visible={tipPopupStatus}
                    title="请前往vivo社区App端投票吧"
                    confirmText="打开App端"
                    onCancelChange={this.onHidePopup}
                    onConfirmButtonChange={this.handleOpenAppConfirm}
                />

                <SharePosterPopup
                    visible={sharePosterVisible}
                    workData={shareWorkItem}
                    photographyDetail={photographyDetail}
                    shareUrl={this.getShareUrl(shareWorkItem)}
                    topVoteCount={this.getTopVoteCount()}
                    onClose={this.closeSharePoster}
                />
            </div>
        );
    }
}

export default withRouter(PhotographySearchH5Page);
